import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAlcoholType } from "./enums/enumAlcoholType.generated";
import { EnumDocumentFileType } from "./enums/enumDocumentFileType.generated";
import { EnumEireMovementType } from "./enums/enumEireMovementType.generated";
import { EnumGermanBeerDutyMovementType } from "./enums/enumGermanBeerDutyMovementType.generated";
import { EnumGermanBeerDutyProductType } from "./enums/enumGermanBeerDutyProductType.generated";
import { EnumSpiritDutyExemptionReason } from "./enums/enumSpiritDutyExemptionReason.generated";
import { EnumStockAdjustDutyImplication } from "./enums/enumStockAdjustDutyImplication.generated";

// Model Objects

export class StockAdjustmentHeaderReadonly extends ModelObject<StockAdjustmentHeaderReadonly> {

    /**
    * Created By Id
    */
    public createdByUserId: Guid = Guid.createEmpty();
    /**
    * Adjustment Number - Auto allocated from numbering scheme.
    * Not Nullable
    * Preserves leading and trailing whitespce.
    */
    public adjustmentNumber: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdByUserId = Guid.parseOrEmpty(jsonSerializedObject.createdByUserId);
          this.adjustmentNumber = jsonSerializedObject.adjustmentNumber;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdByUserId = this.createdByUserId.toString();
        toRet.adjustmentNumber = this.adjustmentNumber;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "createdByUserId", rule: (v: any) => !v.isEmpty() || "StockAdjustmentHeaderReadonly.CreatedByUserId cannot be empty"},
    ];
}


export class StockAdjustmentHeader extends ModelObject<StockAdjustmentHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Stock Location
    */
    public stockLocationId: Guid = Guid.createEmpty();
    /**
    * Adjustment Date
    */
    public adjustmentDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * Adjustment Reason
    * Not Nullable
    */
    public reason: string = "";
    /**
    * DutyImplication
    */
    public dutyImplication: EnumStockAdjustDutyImplication = EnumStockAdjustDutyImplication.noDuty;
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    public readOnly: StockAdjustmentHeaderReadonly = new StockAdjustmentHeaderReadonly();
    public germanBeerDutyMovementType: EnumGermanBeerDutyMovementType = EnumGermanBeerDutyMovementType.none;
    public eireDutyMovementType: EnumEireMovementType = EnumEireMovementType.unknown;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.adjustmentDate = LocalDate.parse(jsonSerializedObject.adjustmentDate);
          this.reason = jsonSerializedObject.reason;
          this.dutyImplication = EnumStockAdjustDutyImplication.getByValue(jsonSerializedObject.dutyImplication, this.dutyImplication);
          this.notes = jsonSerializedObject.notes;
          this.readOnly = new StockAdjustmentHeaderReadonly(jsonSerializedObject.readOnly);
          this.germanBeerDutyMovementType = EnumGermanBeerDutyMovementType.getByValue(jsonSerializedObject.germanBeerDutyMovementType, this.germanBeerDutyMovementType);
          this.eireDutyMovementType = EnumEireMovementType.getByValue(jsonSerializedObject.eireDutyMovementType, this.eireDutyMovementType);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.adjustmentDate = this.adjustmentDate.toString();
        toRet.reason = this.reason;
        toRet.dutyImplication = this.dutyImplication.value;
        toRet.notes = this.notes;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.germanBeerDutyMovementType = this.germanBeerDutyMovementType.value;
        toRet.eireDutyMovementType = this.eireDutyMovementType.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "StockAdjustmentHeader.Id cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "StockAdjustmentHeader.StockLocationId cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class StockAdjustmentLineReadonly extends ModelObject<StockAdjustmentLineReadonly> {

    /**
    * AlcoholType
    */
    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    /**
    * ItemAbv
    */
    public abv: number = 0;
    /**
    * ItemDutiableLitres
    */
    public unitDutiableLitres: number = 0;
    /**
    * ItemUnitLitres
    */
    public unitLitres: number = 0;
    private _manufacturerId: Guid | null = null;
    /**
    * Manufacturer Id
    */
    public get manufacturerId(): Guid | null { return this._manufacturerId }
    public set manufacturerId(value: Guid | null) {
      this._manufacturerId = value === undefined ? null : value;
    }
    /**
    * Stock Adjustment Id
    */
    public stockAdjustmentId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.abv = jsonSerializedObject.abv;
          this.unitDutiableLitres = jsonSerializedObject.unitDutiableLitres;
          this.unitLitres = jsonSerializedObject.unitLitres;
          this.manufacturerId = jsonSerializedObject.manufacturerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.manufacturerId);
          this.stockAdjustmentId = Guid.parseOrEmpty(jsonSerializedObject.stockAdjustmentId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.alcoholType = this.alcoholType.value;
        toRet.abv = this.abv;
        toRet.unitDutiableLitres = this.unitDutiableLitres;
        toRet.unitLitres = this.unitLitres;
        toRet.manufacturerId = this.manufacturerId == null ? null : this.manufacturerId.toString();
        toRet.stockAdjustmentId = this.stockAdjustmentId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "manufacturerId", rule: (v: any) => v == null || !v.isEmpty() || "StockAdjustmentLineReadonly.ManufacturerId cannot be empty"},
            {field: "stockAdjustmentId", rule: (v: any) => !v.isEmpty() || "StockAdjustmentLineReadonly.StockAdjustmentId cannot be empty"},
    ];
}


export class StockAdjustmentLine extends ModelObject<StockAdjustmentLine> {

    /**
    * Quantity
    */
    public quantity: number = 0;
    /**
    * Unit of Measure
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    private _dateOfBatch: LocalDate | null = null;
    public get dateOfBatch(): LocalDate | null { return this._dateOfBatch }
    public set dateOfBatch(value: LocalDate | null) {
      this._dateOfBatch = value === undefined ? null : value;
    }
    public stockItemId: Guid = Guid.createEmpty();
    public urn: string = "";
    private _hopAlpha: number | null = null;
    public get hopAlpha(): number | null { return this._hopAlpha }
    public set hopAlpha(value: number | null) {
      this._hopAlpha = value === undefined ? null : value;
    }
    public readOnly: StockAdjustmentLineReadonly = new StockAdjustmentLineReadonly();
    private _plato: number | null = null;
    /**
    * Plato Value
    */
    public get plato(): number | null { return this._plato }
    public set plato(value: number | null) {
      this._plato = value === undefined ? null : value;
    }
    public germanBeerDutyProductType: EnumGermanBeerDutyProductType = EnumGermanBeerDutyProductType.none;
    private _germanBeerMixedProductPercentageA: number | null = null;
    public get germanBeerMixedProductPercentageA(): number | null { return this._germanBeerMixedProductPercentageA }
    public set germanBeerMixedProductPercentageA(value: number | null) {
      this._germanBeerMixedProductPercentageA = value === undefined ? null : value;
    }
    /**
    * Set true if this item is brewed by members of the public for their personal use by borrowing the Brewery's equipment. This is a service offered by breweries in some countries, such as Australia, and can affect duty.
    */
    public isBrewedForPersonalUse: boolean = false;
    /**
    * Set true if this item is designed to be connected to a pump or pressurised gas system, such as those used in pubs. Some duty systems have different duty rates for beer packaged in these manners for selling to pubs.
    */
    public isDesignedForPump: boolean = false;
    private _finalUnitLitres: number | null = null;
    /**
    * Litres held by the final consumable packaged unit, if relevant. For example, a case of 12 x 500ml bottles would have a final unit litres of 0.5. Some duty systems have different rates depending upon this value.
    * Greater Than : 0
    */
    public get finalUnitLitres(): number | null { return this._finalUnitLitres }
    public set finalUnitLitres(value: number | null) {
      this._finalUnitLitres = value === undefined ? null : value;
    }
    public spiritDutyExemptionReason: EnumSpiritDutyExemptionReason = EnumSpiritDutyExemptionReason.none;
    public id: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.quantity = jsonSerializedObject.quantity;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.dateOfBatch = jsonSerializedObject.dateOfBatch == null ? null : LocalDate.parse(jsonSerializedObject.dateOfBatch);
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.urn = jsonSerializedObject.urn;
          this.hopAlpha = jsonSerializedObject.hopAlpha == null ? null : jsonSerializedObject.hopAlpha;
          this.readOnly = new StockAdjustmentLineReadonly(jsonSerializedObject.readOnly);
          this.plato = jsonSerializedObject.plato == null ? null : jsonSerializedObject.plato;
          this.germanBeerDutyProductType = EnumGermanBeerDutyProductType.getByValue(jsonSerializedObject.germanBeerDutyProductType, this.germanBeerDutyProductType);
          this.germanBeerMixedProductPercentageA = jsonSerializedObject.germanBeerMixedProductPercentageA == null ? null : jsonSerializedObject.germanBeerMixedProductPercentageA;
          this.isBrewedForPersonalUse = jsonSerializedObject.isBrewedForPersonalUse;
          this.isDesignedForPump = jsonSerializedObject.isDesignedForPump;
          this.finalUnitLitres = jsonSerializedObject.finalUnitLitres == null ? null : jsonSerializedObject.finalUnitLitres;
          this.spiritDutyExemptionReason = EnumSpiritDutyExemptionReason.getByValue(jsonSerializedObject.spiritDutyExemptionReason, this.spiritDutyExemptionReason);
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.quantity = this.quantity;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.dateOfBatch = this.dateOfBatch == null ? null : this.dateOfBatch.toString();
        toRet.stockItemId = this.stockItemId.toString();
        toRet.urn = this.urn;
        toRet.hopAlpha = this.hopAlpha == null ? null : this.hopAlpha;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.plato = this.plato == null ? null : this.plato;
        toRet.germanBeerDutyProductType = this.germanBeerDutyProductType.value;
        toRet.germanBeerMixedProductPercentageA = this.germanBeerMixedProductPercentageA == null ? null : this.germanBeerMixedProductPercentageA;
        toRet.isBrewedForPersonalUse = this.isBrewedForPersonalUse;
        toRet.isDesignedForPump = this.isDesignedForPump;
        toRet.finalUnitLitres = this.finalUnitLitres == null ? null : this.finalUnitLitres;
        toRet.spiritDutyExemptionReason = this.spiritDutyExemptionReason.value;
        toRet.id = this.id.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "StockAdjustmentLine.UnitOfMeasureId cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "StockAdjustmentLine.StockItemId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "finalUnitLitres", rule: (v: any) => v == null || v > 0 || "FinalUnitLitres is not greater than minimum allowed value: 0"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "StockAdjustmentLine.Id cannot be empty"},
    ];
}


export class StockAdjustment extends BaseModelObject<StockAdjustment> {

    public header: StockAdjustmentHeader = new StockAdjustmentHeader();
    public lines: StockAdjustmentLine[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new StockAdjustmentHeader(jsonSerializedObject.header);
          this.lines = jsonSerializedObject.lines.map((v: any) => new StockAdjustmentLine(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as StockAdjustmentLine[]).reduce((acc: Array<string>, curr: StockAdjustmentLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class StockAdjustmentFilter extends ModelObject<StockAdjustmentFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _fromAdjustmentDateInclusive: LocalDate | null = null;
    /**
    * Minimum (inclusive) adjustment date
    */
    public get fromAdjustmentDateInclusive(): LocalDate | null { return this._fromAdjustmentDateInclusive }
    public set fromAdjustmentDateInclusive(value: LocalDate | null) {
      this._fromAdjustmentDateInclusive = value === undefined ? null : value;
    }
    private _toAdjustmentDateInclusive: LocalDate | null = null;
    /**
    * Maximum (inclusive) adjustment date
    */
    public get toAdjustmentDateInclusive(): LocalDate | null { return this._toAdjustmentDateInclusive }
    public set toAdjustmentDateInclusive(value: LocalDate | null) {
      this._toAdjustmentDateInclusive = value === undefined ? null : value;
    }
    private _limitStockAdjustmentIds: Guid[] | null = null;
    public get limitStockAdjustmentIds(): Guid[] | null { return this._limitStockAdjustmentIds }
    public set limitStockAdjustmentIds(value: Guid[] | null) {
      this._limitStockAdjustmentIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.fromAdjustmentDateInclusive = jsonSerializedObject.fromAdjustmentDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.fromAdjustmentDateInclusive);
          this.toAdjustmentDateInclusive = jsonSerializedObject.toAdjustmentDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.toAdjustmentDateInclusive);
          this.limitStockAdjustmentIds = jsonSerializedObject.limitStockAdjustmentIds == null ? null : jsonSerializedObject.limitStockAdjustmentIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.fromAdjustmentDateInclusive = this.fromAdjustmentDateInclusive == null ? null : this.fromAdjustmentDateInclusive.toString();
        toRet.toAdjustmentDateInclusive = this.toAdjustmentDateInclusive == null ? null : this.toAdjustmentDateInclusive.toString();
        toRet.limitStockAdjustmentIds = this.limitStockAdjustmentIds == null ? null : this.limitStockAdjustmentIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredStockAdjustmentResults extends ModelObject<FilteredStockAdjustmentResults> {

    public matchingCount: number = 0;
    public stockAdjustments: StockAdjustmentHeader[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.stockAdjustments = jsonSerializedObject.stockAdjustments.map((v: any) => new StockAdjustmentHeader(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.stockAdjustments = this.stockAdjustments.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "stockAdjustments", rule: (v: any) => !!v || "stockAdjustments is required"},
            {field: "stockAdjustments", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as StockAdjustmentHeader[]).reduce((acc: Array<string>, curr: StockAdjustmentHeader) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class DutySuspendedAdjustmentReceiptsResultSet extends ModelObject<DutySuspendedAdjustmentReceiptsResultSet> {

    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    public dutySuspendedReceivedLitres: number = 0;
    public dutySuspendedReceivedLitresOfAlcohol: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.dutySuspendedReceivedLitres = jsonSerializedObject.dutySuspendedReceivedLitres;
          this.dutySuspendedReceivedLitresOfAlcohol = jsonSerializedObject.dutySuspendedReceivedLitresOfAlcohol;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.alcoholType = this.alcoholType.value;
        toRet.dutySuspendedReceivedLitres = this.dutySuspendedReceivedLitres;
        toRet.dutySuspendedReceivedLitresOfAlcohol = this.dutySuspendedReceivedLitresOfAlcohol;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class PartialStockAdjustment extends ModelObject<PartialStockAdjustment> {

    public header: StockAdjustmentHeader = new StockAdjustmentHeader();
    public lines: StockAdjustmentLine[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new StockAdjustmentHeader(jsonSerializedObject.header);
          this.lines = jsonSerializedObject.lines.map((v: any) => new StockAdjustmentLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as StockAdjustmentLine[]).reduce((acc: Array<string>, curr: StockAdjustmentLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class StockAdjustmentService extends ServiceBase {

  public async getAllStockAdjustmentHeaders(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockAdjustmentHeader[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.stockAdjustments.map((v: any) => new StockAdjustmentHeader(v));;
    return this.makeJsonRequest("stockAdjustment/v1/getAllStockAdjustmentHeaders", req, false, extractor, opts);
  };

  public async getFilteredStockAdjustmentHeaders(tenantId: Guid, filter: StockAdjustmentFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredStockAdjustmentResults> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredStockAdjustmentResults(response.results);;
    return this.makeJsonRequest("stockAdjustment/v1/getFilteredStockAdjustmentHeaders", req, false, extractor, opts);
  };

  public async getStockAdjustments(tenantId: Guid, stockAdjustmentIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockAdjustment[]> {
    const req = {
    tenantId:tenantId.toString(),
    stockAdjustmentIds:stockAdjustmentIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.stockAdjustments.map((v: any) => new StockAdjustment(v));;
    return this.makeJsonRequest("stockAdjustment/v1/getStockAdjustments", req, false, extractor, opts);
  };

  public async createStockAdjustment(tenantId: Guid, stockAdjustment: StockAdjustment, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    stockAdjustment:stockAdjustment.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("stockAdjustment/v1/createStockAdjustment", req, true, extractor, opts);
  };

  public async getDutySuspendedReceiptStockAdjustments(tenantId: Guid, fromDateInclusive: LocalDate, toDateInclusive: LocalDate, limitToGivenAlcoholTypes: boolean, limitedAlcoholTypes: readonly EnumAlcoholType[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockAdjustment[]> {
    const req = {
    tenantId:tenantId.toString(),
    fromDateInclusive:fromDateInclusive.toString(),
    toDateInclusive:toDateInclusive.toString(),
    limitToGivenAlcoholTypes:limitToGivenAlcoholTypes,
    limitedAlcoholTypes:limitedAlcoholTypes.map(v => v.value),
    };
    const extractor = (response:any) => response.results.map((v: any) => new StockAdjustment(v));;
    return this.makeJsonRequest("stockAdjustment/v1/getDutySuspendedReceiptStockAdjustments", req, false, extractor, opts);
  };

  public async evaluateDutySuspendedAdjustmentReceipts(tenantId: Guid, fromDutyDateInclusive: LocalDate, toDutyDateInclusive: LocalDate, limitToGivenAlcoholTypes: boolean, limitedAlcoholTypes: readonly EnumAlcoholType[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<DutySuspendedAdjustmentReceiptsResultSet[]> {
    const req = {
    tenantId:tenantId.toString(),
    fromDutyDateInclusive:fromDutyDateInclusive.toString(),
    toDutyDateInclusive:toDutyDateInclusive.toString(),
    limitToGivenAlcoholTypes:limitToGivenAlcoholTypes,
    limitedAlcoholTypes:limitedAlcoholTypes.map(v => v.value),
    };
    const extractor = (response:any) => response.results.map((v: any) => new DutySuspendedAdjustmentReceiptsResultSet(v));;
    return this.makeJsonRequest("stockAdjustment/v1/evaluateDutySuspendedAdjustmentReceipts", req, false, extractor, opts);
  };

  public async getPossibleDutiableStockAdjustmentLines(tenantId: Guid, fromDateInclusive: LocalDate, toDateInclusive: LocalDate, limitedAlcoholTypes: readonly EnumAlcoholType[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PartialStockAdjustment[]> {
    const req = {
    tenantId:tenantId.toString(),
    fromDateInclusive:fromDateInclusive.toString(),
    toDateInclusive:toDateInclusive.toString(),
    limitedAlcoholTypes:limitedAlcoholTypes.map(v => v.value),
    };
    const extractor = (response:any) => response.results.map((v: any) => new PartialStockAdjustment(v));;
    return this.makeJsonRequest("stockAdjustment/v1/getPossibleDutiableStockAdjustmentLines", req, false, extractor, opts);
  };

  public async generateStockAdjustmentSheet(tenantId: Guid, documentFormat: EnumDocumentFileType, stockAdjustmentId: Guid, documentTemplateId: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:tenantId.toString(),
    documentFormat:documentFormat.value,
    stockAdjustmentId:stockAdjustmentId.toString(),
    documentTemplateId:documentTemplateId == null ? null : documentTemplateId.toString(),
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.documentBytes);;
    return this.makeJsonRequest("stockAdjustment/v1/generateStockAdjustmentSheet", req, false, extractor, opts);
  };
}

export const stockAdjustmentService = new StockAdjustmentService();